/**
 * API Key Management Page
 * Allows users to create, view, and revoke API keys.
 * API keys are scoped to specific capabilities selected at creation time.
 * SECURITY: Raw API keys are NEVER stored - only shown once at creation.
 */

import { Button, CircularProgress } from '@mui/material';
import { Plus } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { listApiKeys, revokeApiKey, rotateApiKey } from '../services/api';
import type { ApiKey, CreateApiKeyResponse } from '../types';
import { useNotification } from '../hooks/useNotification';
import { useTranslation } from 'react-i18next';
import { ApiKeyDetailDialog } from '../components/api-keys/ApiKeyDetailDialog';
import { ApiKeysTable } from '../components/api-keys/ApiKeysTable';
import {
  RevokeConfirmationDialog,
  RotateConfirmationDialog,
} from '../components/api-keys/ConfirmationDialogs';
import { CreateApiKeyModal } from '../components/api-keys/CreateApiKeyModal';
import { IntegrationClientsPanel } from '../components/api-keys/IntegrationClientsPanel';
import { OneTimeKeyModal } from '../components/api-keys/OneTimeKeyModal';
import { PersonalApiKeyUsagePanel } from '../components/api-keys/PersonalApiKeyUsagePanel';
import { ThirdPartyUsagePanel } from '../components/api-keys/ThirdPartyUsagePanel';

export const ApiKeyManagement: React.FC = () => {
  const { t } = useTranslation();
  const { success, error } = useNotification();
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [oneTimeKeyModal, setOneTimeKeyModal] = useState<{
    open: boolean;
    data: CreateApiKeyResponse | null;
  }>({ open: false, data: null });
  const [revokeDialog, setRevokeDialog] = useState<{
    open: boolean;
    apiKey: ApiKey | null;
  }>({ open: false, apiKey: null });
  const [rotateDialog, setRotateDialog] = useState<{
    open: boolean;
    apiKey: ApiKey | null;
  }>({ open: false, apiKey: null });
  const [selectedApiKey, setSelectedApiKey] = useState<ApiKey | null>(null);

  useEffect(() => {
    loadApiKeys(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initial load shows the full-page spinner; later reloads only refresh the table.
  const loadApiKeys = async (initial = false) => {
    if (initial) setLoading(true);
    else setTableLoading(true);
    const response = await listApiKeys();
    if (response.success && response.data) {
      setApiKeys(response.data);
    } else {
      error(response.error || t('apiKeys.errors.loadFailed'));
    }
    setLoading(false);
    setTableLoading(false);
  };

  const handleCopyKey = async (key: string) => {
    try {
      await navigator.clipboard.writeText(key);
      success(t('apiKeys.messages.copied'));
    } catch {
      error(t('apiKeys.errors.copyFailed'));
    }
  };

  // ============================================
  // Render
  // ============================================

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center p-4 md:p-6 lg:p-8">
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 md:mb-8 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-slate-100 md:text-3xl">
            {t('apiKeys.title')}
          </h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-slate-400 md:text-base">
            {t('apiKeys.subtitle')}
          </p>
        </div>
        <Button
          variant="contained"
          startIcon={<Plus size={20} />}
          onClick={() => setCreateModalOpen(true)}
          sx={{ minHeight: 44 }}
        >
          {t('apiKeys.create')}
        </Button>
      </div>

      <IntegrationClientsPanel />
      <PersonalApiKeyUsagePanel apiKeys={apiKeys} />
      <ThirdPartyUsagePanel />

      <ApiKeysTable
        apiKeys={apiKeys}
        loading={tableLoading}
        onCreate={() => setCreateModalOpen(true)}
        onSelect={setSelectedApiKey}
        onRotate={(apiKey) => setRotateDialog({ open: true, apiKey })}
        onRevoke={(apiKey) => setRevokeDialog({ open: true, apiKey })}
      />

      {/* Create Modal */}
      <CreateApiKeyModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={(data) => {
          setCreateModalOpen(false);
          setOneTimeKeyModal({ open: true, data });
          loadApiKeys();
        }}
      />

      {/* One-Time Key Display Modal */}
      <OneTimeKeyModal
        open={oneTimeKeyModal.open}
        data={oneTimeKeyModal.data}
        onClose={() => setOneTimeKeyModal({ open: false, data: null })}
        onCopy={handleCopyKey}
      />

      <ApiKeyDetailDialog
        apiKey={selectedApiKey}
        open={Boolean(selectedApiKey)}
        onClose={() => setSelectedApiKey(null)}
        onRotate={(apiKey) => {
          setSelectedApiKey(null);
          setRotateDialog({ open: true, apiKey });
        }}
        onRevoke={(apiKey) => {
          setSelectedApiKey(null);
          setRevokeDialog({ open: true, apiKey });
        }}
      />

      {/* Revoke Confirmation Dialog */}
      <RevokeConfirmationDialog
        open={revokeDialog.open}
        apiKey={revokeDialog.apiKey}
        onClose={() => setRevokeDialog({ open: false, apiKey: null })}
        onConfirm={async () => {
          if (revokeDialog.apiKey) {
            const response = await revokeApiKey(revokeDialog.apiKey.publicId);
            if (response.success) {
              success(t('apiKeys.messages.revoked'));
              loadApiKeys();
            } else {
              error(response.error || t('apiKeys.errors.revokeFailed'));
            }
          }
          setRevokeDialog({ open: false, apiKey: null });
        }}
      />

      <RotateConfirmationDialog
        open={rotateDialog.open}
        apiKey={rotateDialog.apiKey}
        onClose={() => setRotateDialog({ open: false, apiKey: null })}
        onConfirm={async () => {
          if (rotateDialog.apiKey) {
            const response = await rotateApiKey(rotateDialog.apiKey.publicId);
            if (response.success && response.data?.apiKey) {
              success(t('apiKeys.messages.rotated'));
              setOneTimeKeyModal({ open: true, data: response.data });
              loadApiKeys();
            } else {
              error(response.error || t('apiKeys.errors.rotateFailed'));
            }
          }
          setRotateDialog({ open: false, apiKey: null });
        }}
      />
    </div>
  );
};
